import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AttachmentField,
  Button,
  DateField,
  Field,
  SearchSelect,
  SideDrawer,
  TextArea,
  readAttachmentContent,
  useAttachments,
  type LocalAttachment,
} from '@evogard/ui';
import type { ExpenseFile } from '@evogard/contract';
import {
  COMPENSATION_DEBT_KINDS,
  COMPENSATION_PAID_DEBT_KINDS,
  EXPENSE_PAYMENT_METHODS,
  EXPENSE_PAYMENT_METHOD_LABELS,
  EXPENSE_REQUEST_STAGE_LABELS,
  contaAzulCatalogOf,
  contaAzulCategoriesFor,
  parseMoneyInput,
  toColumn,
  type ExpensePaymentMethod,
  type ExpenseRequestStage,
} from '@evogard/domain';
import type { LaunchExpense } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { useClaimExpenseMutations } from './useClaimExpenses.js';

const PAYMENT_OPTIONS = EXPENSE_PAYMENT_METHODS.map((m) => ({
  value: m,
  label: EXPENSE_PAYMENT_METHOD_LABELS[m],
}));

type DebtKind = NonNullable<LaunchExpense['debt']>['kind'];

/** Os débitos que se pagam a um credor — os únicos que viram despesa. */
const DEBT_OPTIONS = COMPENSATION_PAID_DEBT_KINDS.map((k) => ({
  value: k,
  label: COMPENSATION_DEBT_KINDS[k],
}));

/**
 * Lançar despesa (26/10/2026) — a gaveta da mesa, aberta pela aba Despesas.
 *
 * Sinistro (ou acionamento) e departamento vêm do card e aparecem fixos: não
 * se trocam. **O operador escolhe o beneficiário** na lista do cadastro; a
 * mesa não sugere. A categoria é a do catálogo do Conta Azul da base
 * (`CONTAAZUL_CATALOGS`), só as do departamento e as gerais (28/10/2026); a
 * base sem catálogo mostra o campo desabilitado e lança sem categoria.
 *
 * O documento a pagar e a nota fiscal são enviados ali, na hora, num campo de
 * anexo comum (29/10/2026, pedido do dono — antes se escolhia um anexo que o
 * card já tinha). O arquivo enviado passa a ser anexo do card.
 */
export function LaunchExpenseDrawer(props: LaunchExpenseProps) {
  return (
    <SideDrawer
      eyebrow={`${props.cardLabel} · ${EXPENSE_REQUEST_STAGE_LABELS[props.stage]}`}
      title="Lançar despesa"
      onClose={props.onClose}
      closeLabel="Fechar o lançamento"
    >
      <LaunchExpenseForm {...props} />
    </SideDrawer>
  );
}

interface LaunchExpenseProps {
  stage: ExpenseRequestStage;
  originId: number;
  /** "Sinistro #214", "Periféricos #31". */
  cardLabel: string;
  /**
   * A despesa é um débito da Indenização (29/10/2026): o sinistro está no
   * Pagamento de Débito, e toda despesa lançada ali desconta da FIPE. A gaveta
   * pede então o tipo do débito e a referência. A API confere a fase de novo.
   */
  debt?: boolean;
  /** Fecha a gaveta (ou o formulário da ficha) — também depois de lançar. */
  onClose: () => void;
}

/**
 * O formulário de lançar, sem a moldura: na mesa ele mora na gaveta; na ficha
 * do aviso, que não é mesa, mora dentro da aba Despesas.
 */
export function LaunchExpenseForm({
  stage,
  originId,
  cardLabel,
  debt = false,
  onClose,
}: LaunchExpenseProps) {
  const { data: context } = useSessionContext();
  const canListBeneficiaries = can(context, 'platform.beneficiaries', 'ver');
  const { launch } = useClaimExpenseMutations();

  const [beneficiary, setBeneficiary] = useState('');
  const [beneficiaryLabel, setBeneficiaryLabel] = useState('');
  const [term, setTerm] = useState('');
  const search = useDebouncedTerm(term.trim());
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<ExpensePaymentMethod | ''>('');
  const slug = context?.currentTenant.slug ?? '';
  const hasCatalog = contaAzulCatalogOf(slug) !== null;
  const categoryOptions = contaAzulCategoriesFor(slug, stage).map((c) => ({
    value: c.id,
    label: c.name,
  }));
  const [category, setCategory] = useState('');
  const [competency, setCompetency] = useState('');
  const [due, setDue] = useState('');
  const document = useAttachments();
  const taxInvoice = useAttachments();
  const [notes, setNotes] = useState('');
  const [debtKind, setDebtKind] = useState<DebtKind | ''>('');
  const [referenceOn, setReferenceOn] = useState('');
  const [tried, setTried] = useState(false);

  const beneficiaries = useQuery({
    queryKey: ['claims', 'expenses', 'beneficiaries', context?.currentTenant.id, search],
    enabled: canListBeneficiaries,
    queryFn: async () =>
      ok(
        await api.beneficiaries.listBeneficiaries({
          query: { page: 1, perPage: 30, ...(search ? { search } : {}) },
        }),
        200,
        'Não foi possível carregar os beneficiários.',
      ),
  });

  const parsed = amount.trim() === '' ? null : parseMoneyInput(amount);
  const errors = {
    beneficiary: beneficiary === '' ? 'Escolha quem recebe.' : null,
    amount:
      amount.trim() === ''
        ? 'Informe o valor.'
        : parsed === null || parsed <= 0
          ? 'Um valor em reais maior que zero, como 1.234,56.'
          : null,
    method: method === '' ? 'Escolha a forma de pagamento.' : null,
    category: hasCatalog && category === '' ? 'Escolha a categoria.' : null,
    competency: competency === '' ? 'Informe a competência.' : null,
    due: due === '' ? 'Informe o vencimento.' : null,
    document: document.items.length === 0 ? 'Envie o documento a pagar.' : null,
    debtKind: debt && debtKind === '' ? 'Escolha o tipo do débito.' : null,
    notes:
      debt && debtKind === 'outros' && notes.trim() === ''
        ? 'No débito "Outros", diga aqui o que é.'
        : null,
  };
  const errorOf = (k: keyof typeof errors) => (tried ? errors[k] : null);

  /* O tipo é o que a allowlist resolveu na escolha, e não o palpite do aparelho. */
  const toBody = async (chosen: LocalAttachment): Promise<ExpenseFile> => ({
    upload: {
      fileName: chosen.file.name,
      mimeType: chosen.typeMime,
      content: await readAttachmentContent(chosen.file),
    },
  });

  const submit = async () => {
    setTried(true);
    const documentFile = document.items[0];
    if (Object.values(errors).some((e) => e !== null) || parsed === null || !documentFile)
      return;
    await launch.mutateAsync({
      stage,
      originId,
      body: {
        beneficiaryId: Number(beneficiary),
        amount: toColumn(parsed),
        paymentMethod: method as ExpensePaymentMethod,
        category: hasCatalog ? category : null,
        competencyDate: competency,
        dueDate: due,
        document: await toBody(documentFile),
        taxInvoice: taxInvoice.items[0] ? await toBody(taxInvoice.items[0]) : null,
        notes: notes.trim() || null,
        debt: debt
          ? { kind: debtKind as DebtKind, referenceOn: referenceOn || null }
          : null,
      },
    });
    onClose();
  };

  return (
    <div className="flex flex-col gap-3.5">
      <p className="text-sm text-texto-suave">
        A despesa nasce em Solicitação, com o supervisor de{' '}
        {EXPENSE_REQUEST_STAGE_LABELS[stage]}. Depois que ele aprova, ela vai ao
        Financeiro.
      </p>
      {debt && (
        <Alert tone="informacao">
          O sinistro está no Pagamento de Débito: esta despesa é um débito e desconta da
          FIPE da indenização desde já. Se o supervisor reprovar ou o Financeiro recusar,
          ela deixa de descontar.
        </Alert>
      )}
      {launch.isError && <Alert tone="erro">{launch.error.message}</Alert>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Card" value={cardLabel} readOnly disabled />
        <Field
          label="Departamento"
          value={EXPENSE_REQUEST_STAGE_LABELS[stage]}
          readOnly
          disabled
        />
      </div>
      {debt && (
        <div className="grid grid-cols-2 gap-3">
          <SearchSelect
            label="Tipo do débito"
            required
            options={DEBT_OPTIONS}
            value={debtKind}
            onChange={(v) => setDebtKind(v as DebtKind)}
            error={errorOf('debtKind')}
            help="Mensalidade, despachante e depreciação só descontam: entram pelo formulário, em Incluir desconto."
          />
          <DateField
            label="Referência"
            layer="modal"
            value={referenceOn}
            onChange={setReferenceOn}
            help="A data da multa, o ano do IPVA."
          />
        </div>
      )}
      {canListBeneficiaries ? (
        <SearchSelect
          label="Beneficiário"
          required
          options={(beneficiaries.data?.items ?? []).map((b) => ({
            value: String(b.id),
            label: b.owner.readable
              ? `${b.owner.name}${b.pixKey ? ' · PIX' : ''}`
              : `Beneficiário #${b.id}`,
          }))}
          value={beneficiary}
          onChange={(v) => {
            setBeneficiary(v);
            const found = beneficiaries.data?.items.find((b) => String(b.id) === v);
            setBeneficiaryLabel(found?.owner.name ?? `Beneficiário #${v}`);
          }}
          onSearchChange={setTerm}
          selectedLabel={beneficiaryLabel || undefined}
          loading={beneficiaries.isLoading}
          error={
            errorOf('beneficiary') ??
            (beneficiaries.isError ? beneficiaries.error.message : null)
          }
          help="Quem recebe, com os dados bancários do cadastro de beneficiários."
        />
      ) : (
        <Alert tone="informacao">
          Escolher o beneficiário pede a permissão de ver o cadastro de beneficiários
          (Plataforma › Cadastros › Contas de pagamento), que você não tem. Peça a quem
          administra os grupos.
        </Alert>
      )}
      {hasCatalog ? (
        <SearchSelect
          label="Categoria"
          required
          options={categoryOptions}
          value={category}
          onChange={setCategory}
          error={errorOf('category')}
          help={`As categorias do Conta Azul desta base para ${EXPENSE_REQUEST_STAGE_LABELS[stage]}, e as gerais. O Financeiro confere ao autorizar.`}
        />
      ) : (
        <Field
          label="Categoria"
          value=""
          disabled
          readOnly
          help="As categorias do Conta Azul desta base ainda não estão na plataforma."
        />
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Valor"
          required
          prefix="R$"
          mask="money"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={errorOf('amount')}
        />
        <SearchSelect
          label="Forma de pagamento"
          required
          options={PAYMENT_OPTIONS}
          value={method}
          onChange={(v) => setMethod(v as ExpensePaymentMethod)}
          error={errorOf('method')}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <DateField
          label="Competência"
          required
          layer="modal"
          value={competency}
          onChange={setCompetency}
          error={errorOf('competency')}
        />
        <DateField
          label="Vencimento"
          required
          layer="modal"
          value={due}
          onChange={setDue}
          error={errorOf('due')}
        />
      </div>
      <div className="flex flex-col gap-1">
        <AttachmentField
          label={
            <span>
              Documento a pagar <span className="text-erro">*</span>
            </span>
          }
          items={document.items}
          onAdd={document.add}
          onRemove={document.remove}
          max={1}
          disabled={launch.isPending}
        />
        {errorOf('document') && (
          <p className="text-xs text-erro">{errorOf('document')}</p>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <AttachmentField
          label="Nota fiscal"
          items={taxInvoice.items}
          onAdd={taxInvoice.add}
          onRemove={taxInvoice.remove}
          max={1}
          disabled={launch.isPending}
        />
        <p className="text-xs text-texto-suave">
          Se ainda não chegou, a nota é anexada depois, na despesa.
        </p>
      </div>
      <TextArea
        label="Observações"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        maxLength={2000}
        rows={3}
        required={debt && debtKind === 'outros'}
        error={errorOf('notes')}
      />
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secundario" onClick={onClose} disabled={launch.isPending}>
          Cancelar
        </Button>
        <Button loading={launch.isPending} onClick={() => void submit()}>
          Lançar despesa
        </Button>
      </div>
    </div>
  );
}
